import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Info
} from 'lucide-react';

import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import { wordpressService } from '../../services/wordpressService';
import Button from '../../components/ui/Button';
import { useModal } from '../../components/ui/ModalProvider';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { useRequestGuard } from '../../hooks/useRequestGuard';

export default function Sites() {
  const navigate = useNavigate();
  const { confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startSitesRequest = useRequestGuard();

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [testingConnection, setTestingConnection] = useState(null);
  const [siteDiagnostics, setSiteDiagnostics] = useState({});

  // Clear the previous profile's sites immediately on switch so stale
  // content can't flash while the new profile's data is loading.
  useEffect(() => {
    setSites([]);
    setLoading(true);
  }, [activeProfile?.id]);

  useEffect(() => {
    loadSites();
  }, [activeProfile?.id]);

  const loadSites = async () => {
    const isCurrent = startSitesRequest();
    try {
      setError('');
      const data = await wordpressService?.getAllSites(activeProfile?.id);
      if (!isCurrent()) return;
      setSites(data);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err?.message || 'Failed to load sites');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const handleTestConnection = async (siteId) => {
    setTestingConnection(siteId);
    setError('');

    try {
      const result = await wordpressService?.testGymPlixConnection(siteId);

      setSiteDiagnostics(prev => ({
        ...prev,
        [siteId]: result
      }));

      await loadSites();

      if (!result?.healthCheck || !result?.auth || !result?.categories) {
        setError(result?.message);
      } else {
        setError('');
      }
    } catch (err) {
      const errorMessage = err?.message || 'Failed to test connection';
      setError(errorMessage);

      setSiteDiagnostics(prev => ({
        ...prev,
        [siteId]: {
          healthCheck: false,
          auth: false,
          categories: false,
          statusBadge: '🔴 Error',
          message: errorMessage
        }
      }));
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!(await confirm('Are you sure you want to delete this site?'))) return;

    try {
      await wordpressService?.deleteSite(siteId);
      await loadSites();
    } catch (err) {
      setError(err?.message || 'Failed to delete site');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (siteId) => {
    const diagnostics = siteDiagnostics?.[siteId];
    if (!diagnostics) return null;
    return diagnostics?.statusBadge || '🔴 Error';
  };

  const getStatusClass = (siteId) => {
    const badge = getStatusBadge(siteId);
    if (badge?.includes('🟢')) return 'bg-green-100 text-green-800';
    if (badge?.includes('🟡')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen bg-gray-50">
          <Sidebar />
          <main className="main-content flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-600">Loading sites...</p>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50">
        <Sidebar />

        <main className="main-content">
          <div className="max-w-6xl mx-auto">

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 mr-3 sm:mr-4">
                  <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    WordPress Sites
                  </h1>
                  <p className="text-sm text-gray-600">
                    Manage your connected WordPress sites
                  </p>
                </div>
              </div>

              <Button onClick={() => navigate('/wp/add-site')}>
                <Plus className="w-4 h-4 mr-2" />
                Add Site
              </Button>
            </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Site Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Domain
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        GymPlix Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Last Sync
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {sites?.map(site => (
                      <tr key={site?.id} className="hover:bg-gray-50">

                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            {getStatusIcon(siteDiagnostics?.[site?.id]?.statusBadge)}
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {site?.siteName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {site?.defaultCategory || 'No category'}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <a
                            href={site?.siteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-indigo-600 hover:text-indigo-800"
                          >
                            {site?.siteUrl}
                          </a>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {siteDiagnostics?.[site?.id] ? (
                              <>
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(site?.id)}`}
                                >
                                  {getStatusBadge(site?.id)}
                                </span>

                                <div className="relative group">
                                  <Info className="w-4 h-4 text-gray-400" />
                                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg">
                                    <div>Health Check</div>
                                    <div>Authentication</div>
                                    <div>Categories Sync</div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <span className="text-xs text-gray-500">
                                Not tested
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(site?.lastSync)}
                        </td>

                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleTestConnection(site?.id)}
                              disabled={testingConnection === site?.id}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <RefreshCw
                                className={`w-4 h-4 ${
                                  testingConnection === site?.id
                                    ? 'animate-spin'
                                    : ''
                                }`}
                              />
                            </button>

                            <button
                              onClick={() => handleDeleteSite(site?.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>

                      </tr>
                    ))}
                  </tbody>

                </table>
                </div>
              </div>

          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
